import { usePersistedState } from "@/lib/persistedFilters";
// Hardware inventory app — staff-only lookup + management for every phone,
// ATA, gateway and headset issued or self-provided across every partner and
// customer. Purpose: type a MAC → instantly know owner, warranty status,
// UC-purchased vs self-provided, and RMA status.

import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Cpu, Plus, Search, Pencil, Trash2, Loader2, Boxes, ShieldCheck, ShieldX,
  Building2, Wrench, Save, Upload, ArrowRightLeft, History, FileDown, X,
  Columns3, Download,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { ColumnsToolbar } from "@/components/ColumnsToolbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useTablePagination, TablePagination, PageSizeSelect } from "@/components/TablePagination";

const OWNER_TYPES    = ["hq", "partner", "customer"];
const HARDWARE_TYPES = ["phone", "ata", "gateway", "headset", "conference", "video", "other"];
const SOURCES        = ["uc_purchased", "self_provided"];
const RMA_STATUSES   = ["none", "requested", "shipped", "received", "replaced", "denied"];

const EMPTY = () => ({
  mac_address: "", manufacturer: "", model: "", hardware_type: "phone",
  owner_type: "hq", owner_org_id: "", location: "", partner: "", domain: "",
  source: "uc_purchased", supplier: "", purchase_date: "", warranty_expires_at: "",
  softswitch_extension: "", first_registered_at: "",
  rma_status: "none", rma_ticket: "", rma_notes: "",
  notes: "",
});

// -- Date helpers used by columns.
const fmtDate = (iso) => {
  if (!iso) return "";
  const s = String(iso).split("T")[0];
  return s || "";
};
const fmtDateTime = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(); }
  catch { return String(iso); }
};

// Column registry — order and visibility are user-customisable & persisted
// per user in localStorage (mirrors the DidMaster/Quotes pattern).
//
// `text(r)` produces the plain-string CSV value for the row; `render(r)`
// returns the JSX for the table cell. Keep them consistent — CSV export
// uses `text(r)` verbatim.
const COLUMNS = [
  { key: "mac_display", label: "MAC", sortable: true,
    text: (r) => r.mac_display || "",
    render: (r) => <span className="font-mono text-[11px] text-slate-700">{r.mac_display}</span> },
  { key: "partner", label: "Partner", sortable: true,
    text: (r) => r.partner || "",
    render: (r) => <span className="text-slate-600">{r.partner || "—"}</span> },
  { key: "domain", label: "Domain", sortable: true,
    text: (r) => r.domain || "",
    render: (r) => <span className="text-slate-600">{r.domain || "—"}</span> },
  { key: "softswitch_extension", label: "Ext", sortable: true,
    text: (r) => r.softswitch_extension || "",
    render: (r) => <span className="font-mono text-xs text-slate-600">{r.softswitch_extension || "—"}</span> },
  { key: "manufacturer", label: "Manufacturer", sortable: true,
    text: (r) => r.manufacturer || "",
    render: (r) => <span className="text-slate-700">{r.manufacturer || "—"}</span> },
  { key: "model", label: "Model", sortable: true,
    text: (r) => r.model || "",
    render: (r) => <span className="font-medium text-slate-800">{r.model || "—"}</span> },
  { key: "hardware_type", label: "Type", sortable: true,
    text: (r) => r.hardware_type || "",
    render: (r) => <span className="capitalize text-xs text-slate-600">{r.hardware_type || "—"}</span> },
  { key: "owner", label: "Owner", sortable: true,
    text: (r) => `${r.owner_type || ""}: ${r.owner_name || ""}`,
    render: (r) => (
      <div className="flex items-center gap-1 text-xs">
        <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-medium uppercase ${
          r.owner_type === "hq" ? "bg-slate-100 text-slate-500"
          : r.owner_type === "partner" ? "bg-blue-100 text-blue-700"
          : "bg-emerald-100 text-emerald-700"
        }`}>{r.owner_type}</span>
        <span className="truncate max-w-[140px]" title={r.owner_name}>{r.owner_name || "—"}</span>
      </div>
    ) },
  { key: "location", label: "Location", sortable: true,
    text: (r) => r.location || "",
    render: (r) => <span className="text-xs text-slate-500">{r.location || "—"}</span> },
  { key: "firmware", label: "Firmware", sortable: true,
    text: (r) => r.firmware || "",
    render: (r) => <span className="font-mono text-[11px] text-slate-500">{r.firmware || "—"}</span> },
  { key: "currently_registered", label: "Live", sortable: true,
    text: (r) => r.currently_registered ? "Yes" : (r.currently_registered === false ? "No" : ""),
    render: (r) => r.currently_registered
      ? <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" title="Currently registered" />
      : r.currently_registered === false
        ? <span className="inline-flex h-2 w-2 rounded-full bg-slate-300" title="Not registered" />
        : <span className="text-slate-300">—</span> },
  { key: "first_registered_at", label: "First registered", sortable: true,
    text: (r) => fmtDate(r.first_registered_at),
    render: (r) => <span className="text-xs text-slate-600 tabular-nums">{fmtDate(r.first_registered_at) || "—"}</span> },
  { key: "last_registration_at", label: "Last seen", sortable: true,
    text: (r) => fmtDateTime(r.last_registration_at),
    render: (r) => <span className="text-xs text-slate-500 tabular-nums">{fmtDateTime(r.last_registration_at) || "—"}</span> },
  { key: "warranty_expires_at", label: "Warranty", sortable: true,
    text: (r) => `${r.warranty_status || ""}: ${fmtDate(r.warranty_expires_at)}`,
    render: (r) => <WarrantyPill status={r.warranty_status} expires={r.warranty_expires_at} /> },
  { key: "source", label: "Source", sortable: true,
    text: (r) => r.source || "",
    render: (r) => <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
      r.source === "uc_purchased" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
    }`}>{r.source === "uc_purchased" ? "UC" : "Self"}</span> },
  { key: "rma_status", label: "RMA", sortable: true,
    text: (r) => r.rma_status === "none" ? "" : (r.rma_status || ""),
    render: (r) => <RmaPill status={r.rma_status} /> },
  { key: "registered_ip", label: "IP", sortable: true,
    text: (r) => r.registered_ip || "",
    render: (r) => <span className="font-mono text-[11px] text-slate-500">{r.registered_ip || "—"}</span> },
  { key: "isp", label: "ISP", sortable: true,
    text: (r) => r.isp || "",
    render: (r) => <span className="text-xs text-slate-500">{r.isp || "—"}</span> },
];
const COL_MAP  = Object.fromEntries(COLUMNS.map((c) => [c.key, c]));
const ALL_KEYS = COLUMNS.map((c) => c.key);
// Default visible columns — busy softswitch fields are hidden until enabled.
const DEFAULT_HIDDEN = ["firmware", "last_registration_at", "registered_ip", "isp"];

export default function Hardware() {
  const { user } = useAuth();
  const PREF_KEY = `hardware_cols_v1_${user?.user_id || "anon"}`;

  // URL query params are the source of truth for filters — makes each
  // filtered view a permalink (share links / bookmarks / deep-links from
  // the Manufacturers page all work). Falling back to `all` on missing.
  const [searchParams, setSearchParams] = useSearchParams();
  const paramOr = (key, dflt = "all") => searchParams.get(key) || dflt;

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = usePersistedState("hardware.q", "", searchParams.get("q"));
  const [ownerFilter, setOwnerFilter] = usePersistedState("hardware.owner", "all", searchParams.get("owner_type"));
  const [sourceFilter, setSourceFilter] = usePersistedState("hardware.source", "all", searchParams.get("source"));
  const [rmaFilter, setRmaFilter] = usePersistedState("hardware.rma", "all", searchParams.get("rma_status"));
  const [partnerFilter, setPartnerFilter] = usePersistedState("hardware.partner", "all", searchParams.get("partner"));
  const [manufacturerFilter, setManufacturerFilter] = usePersistedState("hardware.manufacturer", "all", searchParams.get("manufacturer"));
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [transferring, setTransferring] = useState(null);
  const [sort, setSort] = usePersistedState("hardware.sort", { key: "first_registered_at", dir: "desc" });

  // Two-way URL sync: whenever any filter changes, mirror it in the query
  // string. Empty / "all" values are dropped so URLs stay tidy.
  useEffect(() => {
    const next = new URLSearchParams();
    if (q.trim())                     next.set("q", q.trim());
    if (partnerFilter      !== "all") next.set("partner", partnerFilter);
    if (manufacturerFilter !== "all") next.set("manufacturer", manufacturerFilter);
    if (ownerFilter        !== "all") next.set("owner_type", ownerFilter);
    if (sourceFilter       !== "all") next.set("source", sourceFilter);
    if (rmaFilter          !== "all") next.set("rma_status", rmaFilter);
    setSearchParams(next, { replace: true });
  }, [q, partnerFilter, manufacturerFilter, ownerFilter, sourceFilter, rmaFilter, setSearchParams]);

  // Column prefs (order + hidden), persisted per user in localStorage.
  const [order, setOrder] = useState(ALL_KEYS);
  const [hidden, setHidden] = useState(DEFAULT_HIDDEN);
  useEffect(() => {
    try {
      const p = JSON.parse(localStorage.getItem(PREF_KEY) || "{}");
      const ord = Array.isArray(p.order) ? p.order.filter((k) => COL_MAP[k]) : [];
      setOrder([...ord, ...ALL_KEYS.filter((k) => !ord.includes(k))]);
      if (Array.isArray(p.hidden)) setHidden(p.hidden.filter((k) => COL_MAP[k]));
    } catch { /* keep defaults */ }
  }, [PREF_KEY]);
  const persist = (ord, hid) => localStorage.setItem(PREF_KEY, JSON.stringify({ order: ord, hidden: hid }));
  const toggleHidden = (key) => setHidden((h) => {
    const nh = h.includes(key) ? h.filter((k) => k !== key) : [...h, key];
    persist(order, nh); return nh;
  });
  const resetCols = () => { setOrder(ALL_KEYS); setHidden(DEFAULT_HIDDEN); persist(ALL_KEYS, DEFAULT_HIDDEN); };
  // Iter 90n — Partners-style toolbar contract.
  const moveCol = (key, dir) => {
    setOrder((cur) => {
      const idx = cur.indexOf(key);
      const tgt = idx + dir;
      if (idx < 0 || tgt < 0 || tgt >= cur.length) return cur;
      const next = [...cur];
      [next[idx], next[tgt]] = [next[tgt], next[idx]];
      persist(next, hidden);
      return next;
    });
  };
  const toolbarColumns = useMemo(
    () => ALL_KEYS.map((k) => ({ id: k, label: COL_MAP[k]?.label || k, always: k === "mac" })),
    []
  );
  const toolbarVisibleIds = useMemo(
    () => order.filter((k) => !hidden.includes(k)),
    [order, hidden]
  );
  const visibleCols = useMemo(
    () => order.filter((k) => !hidden.includes(k)).map((k) => COL_MAP[k]).filter(Boolean),
    [order, hidden]);

  const load = async () => {
    setLoading(true);
    try {
      // Server-side filters are still cheap (indexed), but partner /
      // manufacturer / search happen client-side against the full set so
      // the top-of-page dropdowns react instantly.
      const params = new URLSearchParams();
      if (ownerFilter  !== "all") params.set("owner_type",  ownerFilter);
      if (sourceFilter !== "all") params.set("source",      sourceFilter);
      if (rmaFilter    !== "all") params.set("rma_status",  rmaFilter);
      params.set("limit", "50000");
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get(`/hardware?${params}`),
        api.get("/hardware/stats"),
      ]);
      setRows(list.items || []);
      setStats(s);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load hardware");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [ownerFilter, sourceFilter, rmaFilter]);

  // Distinct partner/manufacturer lists — computed once per fetch, used to
  // populate the dropdown filters.
  const partnerOpts = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.partner && s.add(r.partner));
    return [...s].sort();
  }, [rows]);
  const manufacturerOpts = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.manufacturer && s.add(r.manufacturer));
    return [...s].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    let out = rows;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      const mac = q.replace(/[^0-9a-fA-F]/g, "").toLowerCase();
      out = out.filter((r) => {
        const hay = [r.mac_display, r.mac_address, r.partner, r.domain,
                     r.manufacturer, r.model, r.owner_name, r.softswitch_extension,
                     r.location, r.registered_ip, r.isp];
        return hay.some((v) => (v || "").toString().toLowerCase().includes(s))
          || (mac && (r.mac_address || "").toLowerCase().includes(mac));
      });
    }
    if (partnerFilter !== "all")      out = out.filter((r) => r.partner === partnerFilter);
    if (manufacturerFilter !== "all") out = out.filter((r) => r.manufacturer === manufacturerFilter);
    out = [...out].sort((a, b) => {
      const col = COL_MAP[sort.key];
      const av = col ? col.text(a) : (a[sort.key] ?? "");
      const bv = col ? col.text(b) : (b[sort.key] ?? "");
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return out;
  }, [rows, q, partnerFilter, manufacturerFilter, sort]);

  const pg = useTablePagination(filtered, 25);

  const del = async (row) => {
    if (!window.confirm(`Delete ${row.mac_display} (${row.model || row.hardware_type})? This soft-deletes — it can be restored.`)) return;
    try {
      await api.delete(`/hardware/${row.hardware_id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  };

  const exportCsv = () => {
    // Hits the server-side streaming export endpoint with the current
    // filters + visible columns. Browser downloads the file directly;
    // no need to hold 27k rows in JS memory just to serialise them.
    const params = new URLSearchParams();
    if (q.trim())                params.set("q", q.trim());
    if (partnerFilter !== "all") params.set("partner", partnerFilter);
    if (manufacturerFilter !== "all") params.set("manufacturer", manufacturerFilter);
    if (ownerFilter  !== "all")  params.set("owner_type",  ownerFilter);
    if (sourceFilter !== "all")  params.set("source",      sourceFilter);
    if (rmaFilter    !== "all")  params.set("rma_status",  rmaFilter);
    // Map visible column keys → server-known keys. `mac_display` → server
    // uses `mac_address` for the raw and formats it in the exporter; `owner`
    // is a UI-only combo → send both raw parts.
    const serverCols = visibleCols.flatMap((c) => {
      if (c.key === "mac_display") return ["mac_address"];
      if (c.key === "owner")       return ["owner_type", "owner_name"];
      return [c.key];
    });
    params.set("columns", serverCols.join(","));
    // Open in a new tab so the auth cookie rides along and the browser
    // handles the Content-Disposition attachment.
    const backend = process.env.REACT_APP_BACKEND_URL;
    window.open(`${backend}/api/hardware/export.csv?${params}`, "_blank");
    toast.success("Export started — download will begin shortly");
  };

  const toggleSort = (key) => setSort((s) => ({
    key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc",
  }));

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6" data-testid="hardware-page">
      <PageHeader testid="hardware-header" title="Endpoints" icon={Cpu}
        subtitle="Master inventory of every phone, ATA, gateway and headset — with warranty and RMA tracking.">
        <Button data-testid="hardware-bulk-button" variant="outline" onClick={() => setBulkOpen(true)}>
          <Upload className="h-4 w-4" /> Bulk upload
        </Button>
        <Button data-testid="hardware-add-button" onClick={() => setEditing("new")}
          className="bg-primary hover:bg-primary/90 text-primary-foreground">
          <Plus className="h-4 w-4" /> Add hardware
        </Button>
      </PageHeader>

      {stats && <StatCards stats={stats} />}

      <Card className="border-slate-200 shadow-sm p-3 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search MAC, partner, domain, model, ext, IP, ISP…"
              className="pl-7 h-9" data-testid="hardware-search" />
          </div>
          <FilterPill label="Partner" value={partnerFilter} onChange={setPartnerFilter}
            options={[{v: "all", l: "Any"}, ...partnerOpts.map((p) => ({v: p, l: p}))]}
            testid="hardware-filter-partner" />
          <FilterPill label="Manufacturer" value={manufacturerFilter} onChange={setManufacturerFilter}
            options={[{v: "all", l: "Any"}, ...manufacturerOpts.map((m) => ({v: m, l: m}))]}
            testid="hardware-filter-manufacturer" />
          <FilterPill label="Owner" value={ownerFilter} onChange={setOwnerFilter}
            options={[{v: "all", l: "Any"}, ...OWNER_TYPES.map((v) => ({v, l: v}))]}
            testid="hardware-filter-owner" />
          <FilterPill label="Source" value={sourceFilter} onChange={setSourceFilter}
            options={[{v: "all", l: "Any"}, ...SOURCES.map((v) => ({v, l: v.replace("_", " ")}))]}
            testid="hardware-filter-source" />
          <FilterPill label="RMA" value={rmaFilter} onChange={setRmaFilter}
            options={[{v: "all", l: "Any"}, ...RMA_STATUSES.map((v) => ({v, l: v}))]}
            testid="hardware-filter-rma" />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 hidden" data-testid="hardware-columns-legacy">
                <Columns3 className="h-4 w-4" /> Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="hidden" />
          </DropdownMenu>
          <PageSizeSelect pageSize={pg.pageSize} setPageSize={pg.setPageSize} testid="hardware-page-size" />
          {/* Iter 90n — shared Columns · CSV · PDF toolbar. */}
          <ColumnsToolbar
            testidPrefix="hardware"
            count={filtered.length}
            columns={toolbarColumns}
            visibleIds={toolbarVisibleIds}
            onToggle={toggleHidden}
            onMove={moveCol}
            onReset={resetCols}
            onExportCsv={exportCsv}
            extraLeft={<span className="text-[11px] text-slate-400 mr-1" data-testid="hardware-count-secondary">of {rows.length.toLocaleString()}</span>}
          />
        </div>
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                {visibleCols.map((c) => (
                  <th key={c.key} className="px-3 py-2 text-left whitespace-nowrap">
                    <button onClick={() => c.sortable && toggleSort(c.key)}
                            className={`inline-flex items-center gap-1 ${c.sortable ? "hover:text-slate-800 cursor-pointer" : "cursor-default"}`}>
                      {c.label}
                      {sort.key === c.key && (
                        <span className="text-primary">{sort.dir === "asc" ? "↑" : "↓"}</span>
                      )}
                    </button>
                  </th>
                ))}
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={visibleCols.length + 1} className="px-3 py-8 text-center text-slate-400">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" /></td></tr>
              ) : pg.pageItems.length === 0 ? (
                <tr><td colSpan={visibleCols.length + 1} className="px-3 py-12 text-center">
                  <Boxes className="mx-auto h-8 w-8 text-slate-300 mb-2" />
                  <div className="text-sm font-semibold text-slate-600">No hardware matches</div>
                  <div className="mt-1 text-xs text-slate-400">Try adjusting the filters, or click <strong>Add hardware</strong> / <strong>Bulk upload</strong>.</div>
                </td></tr>
              ) : pg.pageItems.map((r) => (
                <tr key={r.hardware_id} data-testid={`hardware-row-${r.hardware_id}`}
                    className="hover:bg-slate-50/50">
                  {visibleCols.map((c) => (
                    <td key={c.key} className="px-3 py-2 whitespace-nowrap">{c.render(r)}</td>
                  ))}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setTransferring(r)} data-testid={`hardware-transfer-${r.hardware_id}`}
                      title="Transfer to a different owner"
                      className="p-1 text-slate-400 hover:text-indigo-600"><ArrowRightLeft className="h-3.5 w-3.5" /></button>
                    <button onClick={() => setEditing(r)} data-testid={`hardware-edit-${r.hardware_id}`}
                      className="p-1 text-slate-400 hover:text-slate-700"><Pencil className="h-3.5 w-3.5" /></button>
                    <button onClick={() => del(r)} data-testid={`hardware-delete-${r.hardware_id}`}
                      className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePagination {...pg} testid="hardware-pagination" />
      </Card>

      {editing && <HardwareDialog editing={editing} onClose={() => setEditing(null)} onSaved={load} />}
      {bulkOpen && <BulkUploadDialog onClose={() => setBulkOpen(false)} onDone={load} />}
      {transferring && (
        <TransferDialog row={transferring} onClose={() => setTransferring(null)} onDone={load} />
      )}
    </div>
  );
}

function StatCards({ stats }) {
  const cards = [
    { icon: Boxes,       label: "Total devices",  value: stats.total,        tone: "slate" },
    { icon: ShieldCheck, label: "UC-purchased",   value: stats.uc_purchased, tone: "emerald" },
    { icon: ShieldX,     label: "Self-provided",  value: stats.self_provided,tone: "amber" },
    { icon: Wrench,      label: "Active RMAs",    value: stats.active_rma,   tone: "rose" },
    { icon: Building2,   label: "At partners",    value: stats.at_partners,  tone: "blue" },
    { icon: Building2,   label: "At customers",   value: stats.at_customers, tone: "indigo" },
  ];
  const toneMap = {
    slate:   "text-slate-600 bg-slate-50",
    emerald: "text-emerald-700 bg-emerald-50",
    amber:   "text-amber-700 bg-amber-50",
    rose:    "text-rose-700 bg-rose-50",
    blue:    "text-blue-700 bg-blue-50",
    indigo:  "text-indigo-700 bg-indigo-50",
  };
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
      {cards.map((c) => (
        <div key={c.label} className={`rounded-lg border border-slate-200 bg-white px-3 py-2 flex items-center gap-2`}
             data-testid={`hardware-stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
          <div className={`h-8 w-8 rounded flex items-center justify-center ${toneMap[c.tone]}`}>
            <c.icon className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{c.label}</div>
            <div className="text-lg font-bold text-slate-800 tabular-nums">{c.value}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FilterPill({ label, value, onChange, options, testid }) {
  return (
    <div className="flex items-center gap-1">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-8 min-w-[110px] text-xs capitalize" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.v} value={o.v} className="capitalize">{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function WarrantyPill({ status, expires }) {
  const cfg = {
    covered:   { cls: "bg-emerald-100 text-emerald-700", label: "Covered" },
    expired:   { cls: "bg-slate-100 text-slate-500",     label: "Expired" },
    none:      { cls: "bg-slate-50 text-slate-400 border border-slate-200", label: "None" },
    unknown:   { cls: "bg-amber-50 text-amber-700",      label: "Unknown" },
  }[status] || { cls: "bg-slate-100 text-slate-500", label: status };
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${cfg.cls}`}
          title={expires ? `Expires ${new Date(expires).toLocaleDateString()}` : ""}>
      {cfg.label}
    </span>
  );
}

function RmaPill({ status }) {
  if (!status || status === "none") return <span className="text-slate-300">—</span>;
  const cfg = {
    requested: "bg-amber-100 text-amber-700",
    shipped:   "bg-blue-100 text-blue-700",
    received:  "bg-indigo-100 text-indigo-700",
    replaced:  "bg-emerald-100 text-emerald-700",
    denied:    "bg-rose-100 text-rose-700",
  }[status] || "bg-slate-100 text-slate-500";
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium capitalize ${cfg}`}>{status}</span>;
}

// ---- Add/Edit Dialog ----------------------------------------------------

function HardwareDialog({ editing, onClose, onSaved }) {
  const creating = editing === "new";
  // Seed with the (lean) row from the table, then hydrate with full record
  // (notes, rma_notes, transfer_history…) once the API call returns. Keeps
  // the dialog open instantly rather than blocking on a fetch.
  const [f, setF] = useState(creating ? EMPTY() : { ...EMPTY(), ...editing });
  const [full, setFull] = useState(creating ? null : editing);
  const [saving, setSaving] = useState(false);
  const [orgs, setOrgs] = useState(null);
  const set = (patch) => setF((cur) => ({ ...cur, ...patch }));
  const needsOrg = f.owner_type !== "hq";

  useEffect(() => {
    if (creating || !editing?.hardware_id) return;
    // Fetch the full doc (list endpoint returns a lean projection for speed).
    (async () => {
      try {
        const { data } = await api.get(`/hardware/${editing.hardware_id}`);
        setF((cur) => ({ ...cur, ...data }));
        setFull(data);
      } catch { /* dialog already usable with lean data */ }
    })();
  }, [creating, editing?.hardware_id]);

  useEffect(() => {
    if (!needsOrg) return;
    (async () => {
      try {
        const [{ data: p }, { data: c }] = await Promise.all([
          api.get("/organizations?type=partner"),
          api.get("/organizations?type=customer"),
        ]);
        const combined = [
          ...((p.organizations || p || []).map(o => ({ ...o, _kind: "partner" }))),
          ...((c.organizations || c || []).map(o => ({ ...o, _kind: "customer" }))),
        ].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setOrgs(combined);
      } catch { setOrgs([]); }
    })();
  }, [needsOrg]);

  const scopedOrgs = useMemo(
    () => (orgs || []).filter((o) => o._kind === f.owner_type),
    [orgs, f.owner_type],
  );

  const save = async () => {
    if (!f.mac_address.trim()) { toast.error("MAC address is required"); return; }
    if (needsOrg && !f.owner_org_id) { toast.error(`Pick a ${f.owner_type} organization`); return; }
    setSaving(true);
    try {
      const payload = { ...f };
      if (!needsOrg) payload.owner_org_id = "";
      if (creating) {
        await api.post("/hardware", payload);
        toast.success("Hardware added");
      } else {
        // On edit we don't resend the (immutable) mac; only the mutable fields.
        const { mac_address, hardware_id, ...rest } = payload;
        await api.patch(`/hardware/${editing.hardware_id}`, rest);
        toast.success("Hardware updated");
      }
      onSaved(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hardware-dialog">
        <DialogHeader>
          <DialogTitle>{creating ? "Add hardware" : `Edit ${f.mac_address ? _pretty(f.mac_address) : ""}`}</DialogTitle>
          <DialogDescription>
            {creating ? "Register a new phone, ATA, gateway or headset in the master inventory."
                      : "Update ownership, RMA status, warranty and notes. MAC address is immutable."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <Field label="MAC address" full={!creating}>
            <Input value={f.mac_address} onChange={(e) => set({ mac_address: e.target.value })}
                   placeholder="aa:bb:cc:dd:ee:ff" disabled={!creating}
                   className="font-mono" data-testid="hardware-field-mac" />
          </Field>
          <Field label="Type">
            <Select value={f.hardware_type} onValueChange={(v) => set({ hardware_type: v })}>
              <SelectTrigger data-testid="hardware-field-type"><SelectValue /></SelectTrigger>
              <SelectContent>{HARDWARE_TYPES.map(v =>
                <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Manufacturer">
            <Input value={f.manufacturer} onChange={(e) => set({ manufacturer: e.target.value })}
                   placeholder="Yealink, Polycom, Grandstream…" data-testid="hardware-field-mfr" />
          </Field>
          <Field label="Model" full>
            <Input value={f.model} onChange={(e) => set({ model: e.target.value })}
                   placeholder="T46G, VVX 411…" data-testid="hardware-field-model" />
          </Field>

          <div className="col-span-2 border-t border-slate-100 pt-2 mt-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Ownership</div>
          </div>
          <Field label="Owner type">
            <Select value={f.owner_type} onValueChange={(v) => set({ owner_type: v, owner_org_id: "" })}>
              <SelectTrigger data-testid="hardware-field-ownertype"><SelectValue /></SelectTrigger>
              <SelectContent>{OWNER_TYPES.map(v =>
                <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {needsOrg && (
            <Field label={`${f.owner_type} organization`}>
              <Select value={f.owner_org_id} onValueChange={(v) => set({ owner_org_id: v })}>
                <SelectTrigger data-testid="hardware-field-org">
                  <SelectValue placeholder={orgs === null ? "Loading…" : "Pick an org"} />
                </SelectTrigger>
                <SelectContent>
                  {scopedOrgs.map(o => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Location (free-text)" full>
            <Input value={f.location} onChange={(e) => set({ location: e.target.value })}
                   placeholder="HQ - Calgary · Front desk · Suite 200"
                   data-testid="hardware-field-location" />
          </Field>

          <div className="col-span-2 border-t border-slate-100 pt-2 mt-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Source & warranty</div>
          </div>
          <Field label="Source">
            <Select value={f.source} onValueChange={(v) => set({ source: v })}>
              <SelectTrigger data-testid="hardware-field-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="uc_purchased">UC purchased (warranty)</SelectItem>
                <SelectItem value="self_provided">Self-provided (no warranty)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Supplier">
            <Input value={f.supplier} onChange={(e) => set({ supplier: e.target.value })}
                   placeholder="e.g. Synnex" data-testid="hardware-field-supplier" />
          </Field>
          <Field label="Purchase date">
            <Input type="date" value={f.purchase_date?.slice(0,10) || ""}
                   onChange={(e) => set({ purchase_date: e.target.value })}
                   data-testid="hardware-field-purchased" />
          </Field>
          <Field label="Warranty expires">
            <Input type="date" value={f.warranty_expires_at?.slice(0,10) || ""}
                   onChange={(e) => set({ warranty_expires_at: e.target.value })}
                   disabled={f.source === "self_provided"}
                   data-testid="hardware-field-warranty" />
          </Field>

          <div className="col-span-2 border-t border-slate-100 pt-2 mt-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">Softswitch registration (auto-populated in future)</div>
          </div>
          <Field label="Extension">
            <Input value={f.softswitch_extension} onChange={(e) => set({ softswitch_extension: e.target.value })}
                   placeholder="Optional" data-testid="hardware-field-ext" />
          </Field>
          <Field label="First registered">
            <Input type="date" value={f.first_registered_at?.slice(0,10) || ""}
                   onChange={(e) => set({ first_registered_at: e.target.value })}
                   data-testid="hardware-field-firstreg" />
          </Field>

          <div className="col-span-2 border-t border-slate-100 pt-2 mt-1">
            <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">RMA</div>
          </div>
          <Field label="RMA status">
            <Select value={f.rma_status} onValueChange={(v) => set({ rma_status: v })}>
              <SelectTrigger data-testid="hardware-field-rma-status"><SelectValue /></SelectTrigger>
              <SelectContent>{RMA_STATUSES.map(v =>
                <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="RMA ticket #">
            <Input value={f.rma_ticket} onChange={(e) => set({ rma_ticket: e.target.value })}
                   data-testid="hardware-field-rma-ticket" />
          </Field>
          <Field label="RMA notes" full>
            <Textarea value={f.rma_notes} onChange={(e) => set({ rma_notes: e.target.value })}
                      rows={2} data-testid="hardware-field-rma-notes" />
          </Field>
          <Field label="General notes" full>
            <Textarea value={f.notes} onChange={(e) => set({ notes: e.target.value })}
                      rows={2} data-testid="hardware-field-notes" />
          </Field>

          {!creating && Array.isArray(full?.transfer_history) && full.transfer_history.length > 0 && (
            <div className="col-span-2 border-t border-slate-100 pt-2 mt-1">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2 flex items-center gap-1">
                <History className="h-3 w-3" /> Chain of custody ({full.transfer_history.length})
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1 text-[11px]"
                   data-testid="hardware-edit-history">
                {[...full.transfer_history].reverse().map((h, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-slate-600">
                    <span className="text-slate-400 tabular-nums">
                      {new Date(h.at).toLocaleDateString()}
                    </span>
                    <span>{h.from_owner_name || h.from_owner_type} → {h.to_owner_name || h.to_owner_type}</span>
                    <span className="text-slate-400 truncate">· {h.by_user_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="hardware-dialog-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="hardware-dialog-save"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {creating ? "Add hardware" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={`space-y-1 ${full ? "col-span-2" : ""}`}>
      <label className="text-xs font-medium text-slate-600">{label}</label>
      {children}
    </div>
  );
}

function _pretty(mac) {
  const s = (mac || "").replace(/[^0-9a-fA-F]/g, "").toLowerCase();
  return s.length === 12 ? s.match(/.{2}/g).join(":") : mac;
}

// ---- Bulk CSV upload ----------------------------------------------------

function BulkUploadDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const downloadTemplate = async () => {
    // Fetch the template through axios so auth headers ride along, then
    // trigger a browser download via an in-memory blob URL.
    try {
      const res = await api.get("/hardware/csv-template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "hardware_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download template");
    }
  };

  const doUpload = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/hardware/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created > 0) toast.success(`Imported ${data.created} devices`);
      if (data.errors?.length) toast.warning(`${data.errors.length} rows failed — see below`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) setFile(f);
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="hardware-bulk-dialog">
        <DialogHeader>
          <DialogTitle>Bulk upload hardware</DialogTitle>
          <DialogDescription>
            Upload a CSV to register many devices at once. Duplicate MACs are
            skipped, not overwritten — safe to re-run.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <button onClick={downloadTemplate} data-testid="hardware-bulk-template"
            className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline">
            <FileDown className="h-3.5 w-3.5" /> Download CSV template
          </button>

          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`rounded-lg border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
              dragging ? "border-primary bg-primary/5" : "border-slate-200 hover:border-slate-300"
            }`}
            data-testid="hardware-bulk-dropzone">
            <Upload className="mx-auto h-6 w-6 text-slate-400 mb-1" />
            {file ? (
              <div className="text-sm text-slate-700 font-medium">{file.name}
                <span className="ml-2 text-xs text-slate-400">({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
            ) : (
              <>
                <div className="text-sm text-slate-600">Drop CSV here, or click to browse</div>
                <div className="text-[11px] text-slate-400 mt-0.5">UTF-8 encoded, header row required</div>
              </>
            )}
            <input ref={inputRef} type="file" accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="hidden" data-testid="hardware-bulk-file" />
          </div>

          {result && (
            <div className="rounded-lg border border-slate-200 p-3 text-xs space-y-1"
                 data-testid="hardware-bulk-result">
              <div className="flex gap-4">
                <span className="text-emerald-700 font-semibold">✓ Created: {result.created}</span>
                <span className="text-slate-600">Skipped (duplicates): {result.skipped_duplicates}</span>
                <span className="text-rose-700">Errors: {result.errors?.length || 0}</span>
              </div>
              {result.errors?.length > 0 && (
                <div className="mt-2 max-h-40 overflow-y-auto space-y-0.5">
                  {result.errors.map((er, i) => (
                    <div key={i} className="text-[11px] text-rose-700 font-mono">
                      row {er.row} · {er.mac || "?"} — {er.error}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="hardware-bulk-cancel">
            {result ? "Close" : "Cancel"}
          </Button>
          <Button onClick={doUpload} disabled={!file || uploading}
                  data-testid="hardware-bulk-submit"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---- Transfer ----------------------------------------------------------

function TransferDialog({ row, onClose, onDone }) {
  const [toType, setToType] = useState(row.owner_type === "hq" ? "customer" : "hq");
  const [toOrgId, setToOrgId] = useState("");
  const [toLocation, setToLocation] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [orgs, setOrgs] = useState(null);
  const [history, setHistory] = useState(null);

  const needsOrg = toType !== "hq";

  useEffect(() => {
    // Load orgs once for the picker, and load prior transfer history for context.
    (async () => {
      try {
        const [{ data: p }, { data: c }, { data: h }] = await Promise.all([
          api.get("/organizations?type=partner"),
          api.get("/organizations?type=customer"),
          api.get(`/hardware/${row.hardware_id}/history`),
        ]);
        const combined = [
          ...((p.organizations || p || []).map(o => ({ ...o, _kind: "partner" }))),
          ...((c.organizations || c || []).map(o => ({ ...o, _kind: "customer" }))),
        ].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setOrgs(combined);
        setHistory(h.history || []);
      } catch { setOrgs([]); setHistory([]); }
    })();
  }, [row.hardware_id]);

  const scopedOrgs = useMemo(
    () => (orgs || []).filter((o) => o._kind === toType),
    [orgs, toType],
  );

  const submit = async () => {
    if (needsOrg && !toOrgId) { toast.error(`Pick a ${toType} organization`); return; }
    setSaving(true);
    try {
      await api.post(`/hardware/${row.hardware_id}/transfer`, {
        to_owner_type: toType,
        to_owner_org_id: needsOrg ? toOrgId : "",
        to_location: toLocation,
        reason,
      });
      toast.success("Transferred");
      onDone(); onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Transfer failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="hardware-transfer-dialog">
        <DialogHeader>
          <DialogTitle>Transfer {_pretty(row.mac_address)}</DialogTitle>
          <DialogDescription>
            Move this device to a new owner. The transfer is logged with your
            name and timestamp for a full chain-of-custody trail.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5 text-xs">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Currently at</div>
            <div className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ${
                row.owner_type === "hq" ? "bg-slate-100 text-slate-500"
                : row.owner_type === "partner" ? "bg-blue-100 text-blue-700"
                : "bg-emerald-100 text-emerald-700"
              }`}>{row.owner_type}</span>
              <span className="font-medium">{row.owner_name || "—"}</span>
              {row.location && <span className="text-slate-400">· {row.location}</span>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Transfer to">
              <Select value={toType} onValueChange={(v) => { setToType(v); setToOrgId(""); }}>
                <SelectTrigger data-testid="hardware-transfer-type"><SelectValue /></SelectTrigger>
                <SelectContent>{OWNER_TYPES.map(v =>
                  <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            {needsOrg && (
              <Field label={`${toType} org`}>
                <Select value={toOrgId} onValueChange={setToOrgId}>
                  <SelectTrigger data-testid="hardware-transfer-org">
                    <SelectValue placeholder={orgs === null ? "Loading…" : "Pick an org"} />
                  </SelectTrigger>
                  <SelectContent>
                    {scopedOrgs.map(o => <SelectItem key={o.org_id} value={o.org_id}>{o.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field label="New location (optional)" full>
              <Input value={toLocation} onChange={(e) => setToLocation(e.target.value)}
                     placeholder="Front desk, Suite 200, back stock room…"
                     data-testid="hardware-transfer-location" />
            </Field>
            <Field label="Reason / notes (optional)" full>
              <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Why is this device moving? Ticket #, RMA swap, customer request…"
                        data-testid="hardware-transfer-reason" />
            </Field>
          </div>

          {history && history.length > 0 && (
            <div className="border-t border-slate-100 pt-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5 flex items-center gap-1">
                <History className="h-3 w-3" /> Prior transfers ({history.length})
              </div>
              <div className="max-h-32 overflow-y-auto space-y-1 text-[11px]" data-testid="hardware-transfer-history">
                {history.map((h, i) => (
                  <div key={i} className="flex items-center gap-1.5 text-slate-600">
                    <span className="text-slate-400 tabular-nums">
                      {new Date(h.at).toLocaleDateString()}
                    </span>
                    <span className="text-slate-500">
                      {h.from_owner_name || h.from_owner_type} → {h.to_owner_name || h.to_owner_type}
                    </span>
                    <span className="text-slate-400 truncate">· {h.by_user_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="hardware-transfer-cancel">
            <X className="h-4 w-4" /> Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="hardware-transfer-submit"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />}
            Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
